import { DeliveryPerson, User, UserRole } from '../types/models';
import { userService, deliveryPersonService } from '../services/firebase';

/**
 * Utility functions to ensure proper mapping between users and delivery persons
 * for delivery assignment and display functionality
 */

export interface DeliveryAgentWithUser {
  deliveryPerson: DeliveryPerson;
  user: User | null;
  isLinked: boolean;
  authUserId?: string;
}

/**
 * Get all delivery agents with their linked user accounts
 * This ensures proper mapping for delivery assignment
 */
export const getDeliveryAgentsWithUsers = async (): Promise<DeliveryAgentWithUser[]> => {
  try {
    console.log('🔄 Getting delivery agents with user mappings...');
    
    // Get all delivery persons
    const deliveryPersons = await deliveryPersonService.getDeliveryPersons();
    console.log('✅ Found', deliveryPersons.length, 'delivery persons');
    
    // Get all users with DELIVERY role
    const allUsers = await userService.getUsers();
    const deliveryUsers = allUsers.filter(user => user.role === UserRole.DELIVERY);
    console.log('✅ Found', deliveryUsers.length, 'delivery users');
    
    // Map delivery persons to their user accounts
    const mappedAgents: DeliveryAgentWithUser[] = [];
    
    for (const deliveryPerson of deliveryPersons) {
      // Find the user account linked to this delivery person
      const linkedUser = deliveryUsers.find(user => 
        user.deliveryPersonId === deliveryPerson.id
      );
      
      // Also check by email as a fallback
      const userByEmail = deliveryUsers.find(user => 
        user.email === deliveryPerson.email
      );
      
      const finalUser = linkedUser || userByEmail;
      
      mappedAgents.push({
        deliveryPerson,
        user: finalUser || null,
        isLinked: !!finalUser,
        authUserId: finalUser?.id
      });
      
      if (!finalUser) {
        console.warn('⚠️ Delivery person not linked to user account:', {
          deliveryPersonId: deliveryPerson.id,
          email: deliveryPerson.email,
          name: deliveryPerson.name
        });
      }
    }
    
    console.log('✅ Mapped', mappedAgents.length, 'delivery agents');
    console.log('✅ Linked agents:', mappedAgents.filter(a => a.isLinked).length);
    console.log('⚠️ Unlinked agents:', mappedAgents.filter(a => !a.isLinked).length);
    
    return mappedAgents;
  } catch (error) {
    console.error('❌ Error getting delivery agents with users:', error);
    throw error;
  }
};

/**
 * Verify and fix delivery agent user mappings
 * This function ensures all delivery agents have proper user accounts
 */
export const verifyDeliveryAgentMappings = async (): Promise<{
  totalAgents: number;
  linkedAgents: number;
  unlinkedAgents: number;
  issues: string[];
}> => {
  try {
    console.log('🔍 Verifying delivery agent user mappings...');
    
    const mappedAgents = await getDeliveryAgentsWithUsers();
    const issues: string[] = [];
    
    const totalAgents = mappedAgents.length;
    const linkedAgents = mappedAgents.filter(a => a.isLinked).length;
    const unlinkedAgents = mappedAgents.filter(a => !a.isLinked).length;
    
    // Check for issues
    mappedAgents.forEach(agent => {
      if (!agent.isLinked) {
        issues.push(`Delivery agent "${agent.deliveryPerson.name}" (${agent.deliveryPerson.email}) has no linked user account`);
      }
      
      if (agent.user && !agent.user.deliveryPersonId) {
        issues.push(`User "${agent.user.name}" (${agent.user.email}) missing deliveryPersonId link`);
      }
      
      if (agent.user && agent.user.deliveryPersonId !== agent.deliveryPerson.id) {
        issues.push(`User "${agent.user.name}" has incorrect deliveryPersonId: ${agent.user.deliveryPersonId} (should be ${agent.deliveryPerson.id})`);
      }
    });
    
    const result = {
      totalAgents,
      linkedAgents,
      unlinkedAgents,
      issues
    };
    
    console.log('🔍 Verification complete:', result);
    return result;
  } catch (error) {
    console.error('❌ Error verifying delivery agent mappings:', error);
    throw error;
  }
};

/**
 * Get delivery agent by user ID (for order assignment)
 * This is used when assigning orders to delivery agents
 */
export const getDeliveryAgentByUserId = async (userId: string): Promise<DeliveryPerson | null> => {
  try {
    // Get the user to find their linked delivery person
    const user = await userService.getUser(userId);
    
    if (!user || user.role !== UserRole.DELIVERY) {
      console.warn('⚠️ User not found or not a delivery user:', userId);
      return null;
    }
    
    if (!user.deliveryPersonId) {
      console.warn('⚠️ User has no linked delivery person:', userId);
      return null;
    }
    
    // Get the delivery person record
    const deliveryPerson = await deliveryPersonService.getDeliveryPerson(user.deliveryPersonId);
    
    if (!deliveryPerson) {
      console.warn('⚠️ Delivery person not found:', user.deliveryPersonId);
      return null;
    }
    
    return deliveryPerson;
  } catch (error) {
    console.error('❌ Error getting delivery agent by user ID:', error);
    return null;
  }
};

/**
 * Get user by delivery person ID (for authentication)
 * This is used when a delivery agent logs in
 */
export const getUserByDeliveryPersonId = async (deliveryPersonId: string): Promise<User | null> => {
  try {
    // Get all delivery users and find the one linked to this delivery person
    const allUsers = await userService.getUsers();
    const linkedUser = allUsers.find(user => 
      user.role === UserRole.DELIVERY && user.deliveryPersonId === deliveryPersonId
    );
    
    if (!linkedUser) {
      console.warn('⚠️ No user account found for delivery person:', deliveryPersonId);
      return null;
    }
    
    return linkedUser;
  } catch (error) {
    console.error('❌ Error getting user by delivery person ID:', error);
    return null;
  }
};

/**
 * Fix broken delivery agent user mappings
 * This function attempts to repair missing links
 */
export const fixDeliveryAgentMappings = async (): Promise<{
  fixed: number;
  errors: string[];
}> => {
  try {
    console.log('🔧 Attempting to fix delivery agent user mappings...');
    
    const mappedAgents = await getDeliveryAgentsWithUsers();
    const errors: string[] = [];
    let fixed = 0;
    
    for (const agent of mappedAgents) {
      if (!agent.isLinked) {
        try {
          // Try to find a user by email
          const userByEmail = await userService.getUserByEmail(agent.deliveryPerson.email);
          
          if (userByEmail && userByEmail.role === UserRole.DELIVERY) {
            // Link the user to the delivery person
            await userService.linkDeliveryPerson(userByEmail.id, agent.deliveryPerson.id);
            console.log('✅ Fixed mapping for:', agent.deliveryPerson.name);
            fixed++;
          } else {
            errors.push(`No matching user account found for ${agent.deliveryPerson.name} (${agent.deliveryPerson.email})`);
          }
        } catch (error) {
          errors.push(`Failed to fix mapping for ${agent.deliveryPerson.name}: ${error}`);
        }
      }
    }
    
    console.log('🔧 Fix complete. Fixed:', fixed, 'Errors:', errors.length);
    return { fixed, errors };
  } catch (error) {
    console.error('❌ Error fixing delivery agent mappings:', error);
    throw error;
  }
};

/**
 * Validate delivery agent for order assignment
 * Ensures the agent can receive order assignments
 */
export const validateDeliveryAgentForAssignment = async (deliveryPersonId: string): Promise<{
  isValid: boolean;
  reason?: string;
  deliveryPerson?: DeliveryPerson;
  user?: User;
}> => {
  try {
    // Get delivery person
    const deliveryPerson = await deliveryPersonService.getDeliveryPerson(deliveryPersonId);
    if (!deliveryPerson) {
      return { isValid: false, reason: 'Delivery person not found' };
    }
    
    // Check if delivery person is active
    if (!deliveryPerson.isActive) {
      return { isValid: false, reason: 'Delivery person is inactive', deliveryPerson };
    }
    
    // Get linked user account
    const user = await getUserByDeliveryPersonId(deliveryPersonId);
    if (!user) {
      return { isValid: false, reason: 'No linked user account found', deliveryPerson };
    }
    
    // Check if user account is active
    if (!user.isActive) {
      return { isValid: false, reason: 'User account is inactive', deliveryPerson, user };
    }
    
    return { isValid: true, deliveryPerson, user };
  } catch (error) {
    console.error('❌ Error validating delivery agent:', error);
    return { isValid: false, reason: 'Validation error occurred' };
  }
};
